/** 0..9 e di nuovo 0: l'ultimo coincide col primo, cosi' il giro si richiude senza scatto. */
const STRIP = [0, 1, 2, 3, 4, 5, 6, 7, 8, 9, 0];

/**
 * Sagoma di un importo a quattro cifre intere: "€0000,00", senza punto delle migliaia
 * come le cifre vere. Non e' la lunghezza della cifra che arrivera' -- non la si conosce
 * -- ma quella di un importo tipico, cosi' al cambio il numero vero prende piu' o meno
 * lo stesso spazio.
 */
const SHAPE = ['€', 'd', 'd', 'd', 'd', ',', 'd', 'd'] as const;

/** La sagoma come testo: e' il `value` del FitNumber che ospita i rulli. */
export const SPINNING_AMOUNT_SHAPE = SHAPE.map((ch) => (ch === 'd' ? '0' : ch)).join('');

const REEL_MASK = 'linear-gradient(to bottom, transparent, black 25%, black 75%, transparent)';

interface SpinningAmountProps {
    /** Sfasa i rulli fra un KPI e l'altro: quattro contatori identici sembrerebbero uno solo. */
    seed?: number;
}

/**
 * Segnaposto di un importo mentre i KPI caricano: un contatore a rulli che gira.
 *
 * Ogni cifra e' una striscia verticale 0..9 che scorre dentro una finestra alta una riga.
 * Velocita' e fase cambiano da rullo a rullo (e da KPI a KPI con `seed`), altrimenti le
 * cifre girerebbero allineate e si leggerebbe un numero che scorre, non una slot.
 *
 * Con "riduci movimento" attivo i rulli stanno fermi sullo zero: resta la sagoma grigia,
 * come uno skeleton qualsiasi.
 *
 * Non ha una misura sua: va dentro il FitNumber del numero vero (`BigNumber.valueContent`)
 * e ne eredita carattere e adattamento alla colonna. Con una misura propria la card
 * cambiava altezza di qualche px all'arrivo dei dati. E' un blocco flex alto 1.25em,
 * quanto la riga `leading-tight` del testo che sostituisce.
 */
export const SpinningAmount = ({ seed = 0 }: SpinningAmountProps) => {
    let digit = 0;

    return (
        <div
            className="flex"
            aria-hidden="true"
        >
            {SHAPE.map((ch, i) => {
                if (ch !== 'd') return <span key={i}>{ch}</span>;

                const n = digit++;
                // Durate fra ~0.8s e ~1.4s, fase negativa: partono gia' a meta' giro.
                const duration = 0.8 + ((n * 7 + seed * 3) % 6) * 0.12;
                const delay = -(((n * 5 + seed * 11) % 10) / 10) * duration;

                return (
                    <span
                        key={i}
                        className="relative inline-block h-[1.25em] overflow-hidden"
                        // Sfuma i bordi della finestra: la cifra entra ed esce come da
                        // dietro un tamburo, invece di essere tagliata di netto.
                        style={{ maskImage: REEL_MASK, WebkitMaskImage: REEL_MASK }}
                    >
                        <span
                            className="flex flex-col motion-safe:animate-[reel-spin_1s_linear_infinite]"
                            style={{
                                animationDuration: `${duration}s`,
                                animationDelay: `${delay}s`,
                            }}
                        >
                            {STRIP.map((d, k) => (
                                <span key={k} className="h-[1.25em]">
                                    {d}
                                </span>
                            ))}
                        </span>
                    </span>
                );
            })}
        </div>
    );
};

export default SpinningAmount;
